import React from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Button,
  TextField,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { Formik, Form } from 'formik';
import { ReactComponent as CloseIcon } from '../../../assets/SVG/close-circle-greay.svg';
import { EmbryoEditValues, GradeEnum, IcmTeGradeEnum } from '../EmbryoGradingAi.helper';
import { useTranslation } from 'react-i18next';


interface EditEmbryoInformationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: EmbryoEditValues) => void;
  initialValues?: EmbryoEditValues;
}



const subLabelSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Montserrat',
    fontWeight: 400,
    fontSize: '14px',
    color: '#9E9E9E',
    bgcolor: '#FFFFFF',
    px: 1,
    py: 0.5,
    borderRadius: 2
  },
                      
};


const boxContainerSx={
  width: 480,
  bgcolor: 'background.paper',
  borderRadius: 4,
  mx: 'auto',
  mt: '20%',
  boxShadow: 24,
  outline: 'none',
  overflow: 'hidden',
};

const headerSx = {
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '18px',
  color: '#232323',
};


const closeIconSx = {
  color: '#9E9E9E',
  '&:hover': {
    backgroundColor: 'rgba(158, 158, 158, 0.1)',
  },
};

const inputGradeScoreSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: 2,
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F5F5F5',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Montserrat',
    fontWeight: 400,
    fontSize: '14px',
    color: '#9E9E9E',
    '&.Mui-disabled': {
      color: '#9E9E9E',
      bgcolor: '#FFFFFF',
      px: 1,
      py: 0.5,
      borderRadius: 2
    },
  },
  '& .MuiOutlinedInput-input': {
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '16px',
    color: '#232323',
    '&.Mui-disabled': {
      color: '#232323',
      WebkitTextFillColor: '#232323',
    },
  },
};


const closeSx ={
  flex: 1,
  borderRadius: 3,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '14px',
  color: '#666666',
  borderColor: '#E0E0E0',
  '&:hover': {
    borderColor: '#CCCCCC',
    backgroundColor: '#F5F5F5',
  },
  padding: 1.5,
};


const saveSx ={
  flex: 1,
  borderRadius: 3,
  textTransform: 'none',
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '14px',
  backgroundColor: '#666666',
  '&:hover': {
    backgroundColor: '#555555',
  },
  padding: 1.5,
};






const EditEmbryoInformationModal: React.FC<EditEmbryoInformationModalProps> = ({
  open,
  onClose,
  onSave,
  initialValues = {
    aiViabilityScore: 0,
    grade: '',
    icmGrade: '',
    teGrade: '',
    embryoId : ''
  }
}) => {

  const { t: tAiGrading } = useTranslation('aiGrading_config');



  return (
    <Modal open={open} onClose={onClose}> 
      <Box
        sx={boxContainerSx}
      >
        {/* Header */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
          p={1}      
          px={2}
          mb={2}
          borderBottom={'1px solid rgba(226, 227, 229, 1)'}
        >
          <Typography 
            sx={headerSx}
          >
            { tAiGrading('editEmbryoInformationDetails') }
          </Typography>
          <Tooltip title={tAiGrading('close')} arrow>
            <IconButton 
              onClick={onClose}
              sx={closeIconSx}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
         
        </Box>

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onSave(values);
            onClose();
          }}
        >
          {({ values, handleChange }) => (
            <Form>
              <Box p={3} pt={1}>
                {/* AI Viability Score */}
                <Box mb={3}>
                  <TextField
                    name="aiViabilityScore"
                    label={ tAiGrading('aiViabilitySuccessScore') }
                    type="number"
                    fullWidth
                    value={Number(values.aiViabilityScore).toFixed(2)}
                    onChange={handleChange}
                    inputProps={{
                      min: 0,
                      max: 100,
                      step: 0.01,
                    }}
                    disabled
                    sx={inputGradeScoreSx}
                  />
                </Box>

                {/* Grade Fields */}
                <Box display="flex" gap={2} mb={2}>
                  {/* Grade */}
                  <Box flex={1}>
                    <Autocomplete
                      options={Object.values(GradeEnum)}
                      getOptionLabel={(option) => option.toString()}
                      value={values.grade || ''}
                      onChange={(_, value) => handleChange({ target: { name: 'grade', value } })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="grade"
                          label={tAiGrading('grade')}
                          sx={subLabelSx}
                        />
                      )}
                      disableClearable
                    />

                  </Box>

                  {/* ICM Grade */}
                  <Box flex={1}>
                    <Autocomplete
                      options={Object.values(IcmTeGradeEnum)}
                      getOptionLabel={(option) => option}
                      value={values.icmGrade || ''}
                      onChange={(_, value) => handleChange({ target: { name: 'icmGrade', value } })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="icmGrade"
                          label={tAiGrading('icmGrade')}
                          sx={subLabelSx}
                        />
                      )}
                      disableClearable
                    />
                  </Box>

                  {/* TE Grade */}
                  <Box flex={1}>                         
                    <Autocomplete
                      options={Object.values(IcmTeGradeEnum)}
                      getOptionLabel={(option) => option}
                      value={values.teGrade || ''}
                      onChange={(_, value) => handleChange({ target: { name: 'teGrade', value } })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="teGrade"
                          label={tAiGrading('teGrade')}
                          sx={subLabelSx}
                        />
                      )}
                      disableClearable
                    />
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box 
                display="flex" 
                justifyContent="space-between" 
                gap={2}
                p={3} 
                pt={0}
              >
                <Button 
                  variant="outlined" 
                  onClick={onClose}
                  sx={closeSx}
                >
                  { tAiGrading('cancel') }
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  sx={saveSx}
                >
                  { tAiGrading('save') }
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditEmbryoInformationModal;